// ============================================
// DEXIE DATABASE - Singleton with relationship tables
// ============================================
// Uses Dexie for indexed queries, compound indexes, and schema versioning.
// Only declares the CURRENT version to avoid upgrade conflicts with
// databases that were originally created by raw IndexedDB.

import Dexie from 'dexie';
import { seedPhaseAFeRoot } from './file-explorer-migration';
import {
    clearLocalTenantSessionVerification,
    getVerifiedLocalTenantId,
    markLocalTenantSessionVerified,
    resolveLocalDatabaseIdentity,
} from './local-tenant-database';
import { upgradeVerifiedLegacyLocalData } from './verified-legacy-local-upgrade';

// Use a NEW database name to avoid version conflicts with the old raw IndexedDB.
// The old 'quotemaker_db' was created by raw IndexedDB at version 5.
// Dexie's versioning is incompatible with raw IndexedDB versions.
// Data migration from old DB happens on first load via database.js compatibility layer.
export const LOCAL_DATABASE_IDENTITY = Object.freeze(resolveLocalDatabaseIdentity());
const DB_NAME = LOCAL_DATABASE_IDENTITY.databaseName;

const db = new Dexie(DB_NAME);

// Dexie version 1 - fresh start with all stores.
db.version(1).stores({
    // Core stores (originally from raw IndexedDB v5)
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',

    // Clients (consolidated from separate rjb_client_db)
    clients: 'id, company, contactName, email, lastQuote',

    // Junction: which learnings were used for which projects
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',

    // Persisted AI pipeline execution history
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',

    // Learning lifecycle events
    learning_events: '++id, learningId, projectId, eventType, timestamp',

    // Scope edit audit trail
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',

    // Training progress snapshots
    training_snapshots: '++id, timestamp, projectId',

    // Per-category margin defaults (cost vs quote pricing)
    margin_defaults: 'id',

    // Longitudinal cost accuracy tracking
    pricing_learnings: '++id, projectId, category, created_at'
});

db.version(2).stores({
    // ALL existing stores from version 1
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',

    // NEW: Complete reference quotes for training
    gold_standards: '++id, projectId, projectType, created_at'
});

db.version(3).stores({
    // ALL existing stores from version 2
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    gold_standards: '++id, projectId, projectType, created_at',

    // AI conversation persistence
    ai_conversations: '++id, page, created_at, updated_at',

    // Training sessions - full 3-layer capture (AI attempt + final sent + learnings)
    training_sessions: '++id, projectId, projectType, created_at'
});

db.version(4).stores({
    // ALL existing stores from version 3
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    gold_standards: '++id, projectId, projectType, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',

    // CRM stores
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
});

db.version(5).stores({
    // ALL existing stores from version 4
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    gold_standards: '++id, projectId, projectType, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',

    // Properties (sites/buildings) - between companies and projects
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
});

// Dexie v6: Adds CRM insights stores. Pure additive - no migration needed.
// All v5 stores are preserved verbatim; new stores appear empty on upgrade.
db.version(6).stores({
    // ALL stores from version 5
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    gold_standards: '++id, projectId, projectType, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',

    // CRM Insights - AI-generated portfolio insights
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',
});

// ============================================
// Dexie v7: Memory architecture overhaul
// ============================================
// PURPOSE: Enable Gold Standards as multi-format reference artifacts and
// Learning effectiveness causal attribution.
//
// NEW INDEXES on gold_standards (additive, no data migration):
//   - outcomeStatus: 'won' | 'lost' | 'pending' - wired up from CRM mark won/lost
//   - region: state code (NSW/VIC/QLD/etc) for regional rate-card filtering
//   - year: capture year for temporal weighting / staleness detection
//   - parentVersionId: links re-sends as version chains, prevents duplicates
//   - userVerified: faster filter for curated vs auto-captured records
//   - tags: free-form tags for grouping (multiEntry index for OR-search)
//
// NEW STORE: learning_outcomes - causal attribution.
// Records: was this learning injected into a quote, what was the final accuracy,
// did it correlate with an improvement?
//
// MIGRATION NOTE (CRITICAL):
// gold_standards changed primary key from ++id (numeric auto-increment) to id (string).
// Dexie cannot change a PK type via plain .stores() - we must drop the store and recreate
// it. This means existing v6 auto-captured numeric-id records are LOST on upgrade. The
// migration is acceptable because:
//   1. The data-service-level code already mints string ids ('gs_xxx') and stored them as
//      a free-form field - the numeric ++id was orphaned anyway
//   2. No production users have shipped data yet (this is a freeze-testing build)
//   3. Auto-capture rebuilds the corpus on the next 5-10 sent quotes
// To preserve old records, run this in browser console BEFORE upgrading:
//   const old = await db.gold_standards.toArray(); console.log(JSON.stringify(old));
db.version(7).stores({
    gold_standards: null  // Drop the v6 store explicitly
});

db.version(8).stores({
    // ALL stores from version 6 - PRESERVED VERBATIM
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',

    // RECREATED: gold_standards with string PK + new indexes
    // String 'id' (was '++id') so the data-service-managed string ids ('gs_...') become real PKs
    // *tags = multiEntry index - Dexie matches each element separately, enables OR-search across tags
    gold_standards: 'id, projectId, projectType, outcomeStatus, region, year, parentVersionId, userVerified, *tags, created_at',

    // NEW: causal attribution for learnings
    // Each row records: a learning was injected into a quote, the project's final accuracy,
    // and (later) the project's outcome. Used to compute "this learning improves accuracy by X%".
    learning_outcomes: '++id, learningId, projectId, [learningId+projectId], injectedAt, projectAccuracy, projectOutcome',
});

// ============================================
// Dexie v9: Paired training - input ↔ output learning
// ============================================
// PURPOSE: Enable Gold Standards to capture BOTH the polished sent quote (output)
// AND the messy original inputs (RFQ docs, photos, notes) the human inspector was
// given. This teaches the AI the input → output transformation, not just the
// output style.
//
// PURELY ADDITIVE - no data migration. All v8 stores are preserved verbatim.
// Existing gold_standards records get hasPairedInputs = false (default) and
// continue to work in findRelevant() unchanged.
//
// NEW indexes on gold_standards:
//   - *inputSemanticTags = multiEntry index for OR-search by INPUT-side tags
//     (different from existing *tags which scores the output)
//   - hasPairedInputs = boolean flag indexed so findRelevant can prefer paired records
//
// NEW JSON-blob fields (not indexed, but documented for clarity):
//   - inputContext: { rfqDocs[], notes, photos[], extractedFields, capturedAt }
//   - inputSemanticTags: string[] - tags computed from the INPUT side
//   - transformationFingerprint: string - short hash of the input → output diff
db.version(9).stores({
    // ALL stores from version 8 - PRESERVED VERBATIM
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',
    learning_outcomes: '++id, learningId, projectId, [learningId+projectId], injectedAt, projectAccuracy, projectOutcome',

    // EXTENDED: gold_standards with paired-input indexes
    // Adds *inputSemanticTags (multiEntry) and hasPairedInputs (boolean filter)
    gold_standards: 'id, projectId, projectType, outcomeStatus, region, year, parentVersionId, userVerified, *tags, *inputSemanticTags, hasPairedInputs, created_at',
});

// ============================================
// Dexie v10: Benchmark system - model + prompt A/B testing
// ============================================
// PURPOSE: Track benchmark runs (different model/prompt configs tested against
// gold standards) and saved benchmark configurations. Enables the Karpathy
// auto-loop: propose → execute → evaluate → keep/discard → repeat.
//
// PURELY ADDITIVE - no data migration. All v9 stores preserved verbatim.
db.version(10).stores({
    // ALL stores from version 9 - PRESERVED VERBATIM
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',
    learning_outcomes: '++id, learningId, projectId, [learningId+projectId], injectedAt, projectAccuracy, projectOutcome',
    gold_standards: 'id, projectId, projectType, outcomeStatus, region, year, parentVersionId, userVerified, *tags, *inputSemanticTags, hasPairedInputs, created_at',

    // NEW: Benchmark system
    benchmark_configs: 'id, name, projectId, created_at',
    benchmark_runs: 'id, configId, projectId, goldStandardId, status, created_at, *tags',
});

// ============================================
// Dexie v11: Sync infrastructure + prompt version control
// ============================================
// PURPOSE: Enable structured S3/MinIO sync with content-addressable change
// tracking, and move prompt version history from localStorage (10-version cap)
// to IndexedDB with full history and performance telemetry.
//
// PURELY ADDITIVE - no data migration. All v10 stores preserved verbatim.
db.version(11).stores({
    // ALL stores from version 10 - PRESERVED VERBATIM
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',
    learning_outcomes: '++id, learningId, projectId, [learningId+projectId], injectedAt, projectAccuracy, projectOutcome',
    gold_standards: 'id, projectId, projectType, outcomeStatus, region, year, parentVersionId, userVerified, *tags, *inputSemanticTags, hasPairedInputs, created_at',
    benchmark_configs: 'id, name, projectId, created_at',
    benchmark_runs: 'id, configId, projectId, goldStandardId, status, created_at, *tags',

    // NEW: Sync state - tracks what's been synced to S3/MinIO
    // Each row = one file path's sync status. contentHash enables delta detection.
    sync_state: 'id, projectId, path, lastSyncedAt, contentHash',

    // NEW: Prompt version history (migrated from localStorage on first access)
    // Each row = one version of one prompt. Unlimited history, performance counters.
    prompt_versions: '++id, promptId, version, created_at',
});

// ============================================
// Dexie v12: Strategic Brain - founder strategy directives + brain telemetry
// ============================================
// PURPOSE: Introduce the Strategic Brain system - founder-level commercial
// directives that carry elevated weight in prompt assembly, and telemetry
// tracking which strategies fired per pipeline run.
//
// PURELY ADDITIVE - no data migration. All v11 stores preserved verbatim.
db.version(12).stores({
    // ALL stores from version 11 - PRESERVED VERBATIM
    quotes: 'id, created_at',
    drafts: 'id',
    learnings: 'id, type, category, created_at, useCount, active',
    business_pricing: 'id',
    projects: 'id, created_at, updated_at, status',
    clients: 'id, company, contactName, email, lastQuote',
    project_learnings: '++id, projectId, learningId, stage, appliedAt, [projectId+learningId]',
    pipeline_executions: '++id, projectId, pipelineId, stageName, status, startTime',
    learning_events: '++id, learningId, projectId, eventType, timestamp',
    scope_audit_trail: '++id, projectId, timestamp, [projectId+stage]',
    training_snapshots: '++id, timestamp, projectId',
    margin_defaults: 'id',
    pricing_learnings: '++id, projectId, category, created_at',
    ai_conversations: '++id, page, created_at, updated_at',
    training_sessions: '++id, projectId, projectType, created_at',
    companies: 'id, name, company_type, is_active, created_at',
    company_addresses: '++id, company_id',
    crm_contacts: 'id, company_id, email, is_primary, created_at',
    crm_activities: '++id, company_id, contact_id, project_id, activity_type, due_date, created_at',
    properties: 'id, company_id, strata_plan, suburb, property_type, is_active, created_at',
    property_contacts: '++id, property_id, contact_id, [property_id+contact_id]',
    crm_insights: 'id, scope_type, scope_id, insight_type, severity, generated_at, is_dismissed, [scope_type+scope_id]',
    insight_runs: '++id, scope_type, scope_id, status, started_at, trigger',
    insight_interactions: '++id, insight_id, interaction_type, created_at',
    learning_outcomes: '++id, learningId, projectId, [learningId+projectId], injectedAt, projectAccuracy, projectOutcome',
    gold_standards: 'id, projectId, projectType, outcomeStatus, region, year, parentVersionId, userVerified, *tags, *inputSemanticTags, hasPairedInputs, created_at',
    benchmark_configs: 'id, name, projectId, created_at',
    benchmark_runs: 'id, configId, projectId, goldStandardId, status, created_at, *tags',
    sync_state: 'id, projectId, path, lastSyncedAt, contentHash',
    prompt_versions: '++id, promptId, version, created_at',

    // NEW: Founder strategy directives - standing business-owner rules that carry
    // elevated weight in AI prompt assembly. Activation conditions (JSON blob)
    // determine when each strategy fires based on project context.
    // tenant_id is indexed for strict multi-tenant isolation when lifted to AWS.
    founder_strategies: 'id, tenant_id, category, priority, is_active, [tenant_id+is_active], created_at, updated_at',

    // NEW: Brain telemetry - records which strategies fired (or were skipped)
    // per pipeline run, enabling full transparency in Developer Tools.
    // tenant_id is indexed for strict multi-tenant isolation when lifted to AWS.
    brain_telemetry: '++id, tenant_id, projectId, pipelineRun, [tenant_id+created_at], created_at',
});

// Version 13: Photo Blob storage - separates binary photo data from project records.
// Previously photos were stored as base64 data URLs inline in the project object,
// causing 6MB+ records for projects with 30 photos. This table stores compressed
// image Blobs separately, referenced by ID from the project's photo metadata.
db.version(13).stores({
    // Photo binary data - separated from project records for IndexedDB efficiency
    photo_blobs: '++id, projectId',
}).upgrade(tx => {
    // No data migration in schema upgrade - migration happens lazily via
    // photo-blob-store.js migrateProjectPhotos() when a project is loaded
    console.log('[DB] v13: photo_blobs table created');
});

// Version 14: Upload queue - persistent background upload tracking.
// Decouples artifact creation (fast) from platform upload (slow, background).
// Queue survives tab close and resumes on reload.
db.version(14).stores({
    upload_queue: '++id, projectId, entityType, entityId, status, createdAt, [projectId+status]',
}).upgrade(tx => {
    console.log('[DB] v14: upload_queue table created');
});

// Version 15: App-level settings key-value store.
// Used for global sync folder handle, user preferences, etc.
// Chrome 122+ supports structured cloning of FileSystemDirectoryHandle in IndexedDB.
db.version(15).stores({
    app_settings: 'key',
}).upgrade(tx => {
    console.log('[DB] v15: app_settings table created');
});

// v16: Agent memory - persistent memory across sessions for the AI assistant.
// Project-scoped memories (keyed by projectId) + global memories (projectId='global').
db.version(16).stores({
    agent_memory: '++id, projectId, category, [projectId+category]',
}).upgrade(tx => {
    console.log('[DB] v16: agent_memory table created');
});

// v17: Video Blob storage - mirror of photo_blobs for video binaries.
// Without this, videos imported in Stage 1 only existed as in-memory blob URLs;
// reloading the project killed the URL and broke the Stage 3 video preview
// ("ERR_FILE_NOT_FOUND" on stale blob: URLs). Now videos persist across sessions.
db.version(17).stores({
    video_blobs: '++id, projectId',
}).upgrade(tx => {
    console.log('[DB] v17: video_blobs table created');
});

// v18: Bio entities + embedding columns + attribution trail.
// See the M1 plan for context. Three changes in one schema bump:
//
//   1. business_profile - singleton table holding the business bio.
//      Only 'current' key is expected in practice, but we index by id
//      to keep the Dexie primary-key contract consistent with other stores.
//      The legacy business identity fields (name, abn, licence, etc.)
//      still live on the `business_pricing` record; the bio table owns
//      the NARRATIVE fields (positioning, voice, pricing_reputation, etc.)
//      so the two concerns don't collide. See config/bio-schemas.js.
//
//   2. client_bios - one row per CRM company; `companyId` indexed.
//      Separate table (rather than a column on `clients`) because Dexie
//      stringified schemas don't accept nested object fields and the bio
//      evolves on a different lifecycle to the CRM contact record.
//
//   3. strata_bios - one row per property; indexed by strata plan OR
//      canonical address so either can drive lookup.
//
//   4. No NEW embedding store - embeddings attach to existing rows
//      (learnings, gold_standards, and the three bio tables) as JSON
//      fields at write time, indexed only by the owner id. We don't
//      Dexie-index the vector itself - cosine similarity runs in JS
//      against the in-memory set retrieved by owner id filters.
//      See backend migration 014_bios_and_embeddings.sql for the
//      pgvector counterpart on the Postgres side.
//
// All three new tables have a `provisional` flag (AI-seeded drafts
// awaiting human review per decision #9) and a `updated_at` timestamp
// for the harvest pipeline to use as a staleness signal.
db.version(18).stores({
    business_profile: 'id, updated_at',
    client_bios: 'id, companyId, updated_at, provisional',
    strata_bios: 'id, strataPlan, address, updated_at, provisional',
}).upgrade(tx => {
    console.log('[DB] v18: business_profile, client_bios, strata_bios tables created; embedding fields ready on existing rows');
});

// v19: Proposed Learnings queue (M4) + anti-learnings.
//
// When a quote is saved, marked Won, or (occasionally) marked Lost, the
// M4 harvest pipeline reads the project's before/after diff + context +
// existing learnings, asks the harvest AI to propose candidate learnings,
// and either:
//   • AUTO-COMMITS candidates the AI rates "very high" confidence (status
//     = 'auto_committed', immediately becomes a real learning record too)
//   • QUEUES everything else for human review (status = 'pending')
//
// When the user rejects a proposal via the Proposed Learnings UI, the
// record keeps status='rejected' and becomes an ANTI-LEARNING - the next
// harvest pass reads recent anti-learnings into its prompt context so it
// doesn't re-propose the same pattern the user already shot down.
//
// Schema:
//   • id                : proposal_<ts>_<rand>
//   • status            : 'pending' | 'accepted' | 'rejected' | 'auto_committed' | 'superseded'
//   • source            : 'save-quote' | 'mark-won' | 'mark-lost' | 'manual' | 'assistant'
//   • sourceProjectId   : project id that produced this proposal
//   • proposedAt        : ISO timestamp
//   • confidence        : 0-1 from the harvest AI
//   • autoCommitted     : boolean - true if bypassed the queue
//   • candidate         : { type, category, original, corrected, context, harvestRationale }
//   • committedLearningId : learning id if status=accepted|auto_committed
//   • reviewedBy        : user name on accept/reject
//   • reviewedAt        : ISO
//   • reviewNotes       : free-text from the user on the review action
//   • antiReason        : 'not-general' | 'outdated' | 'duplicate' | 'wrong' | 'other' (status=rejected only)
//
// Indexes: status (queue filtering), sourceProjectId (project audit),
// proposedAt (time ordering), autoCommitted (stats).
db.version(19).stores({
    proposed_learnings: 'id, status, sourceProjectId, proposedAt, autoCommitted',
}).upgrade(tx => {
    console.log('[DB] v19: proposed_learnings table created (M4 capture redesign)');
});

// ============================================
// Dexie v20: Assistant conversation extensions (Wave-2 Ship 2)
// ============================================
// PURPOSE: Mirror backend migration 022 (compacted_summary, compacted_up_to,
// token_count, status) on the client so the assistant panel's Dexie-first
// writes don't drop new fields on reload.
//
// Store SHAPE changes:
//   • Index `status` so queries like "active conversation for this project"
//     are O(log n).
//   • Compound index `[projectId+status]` to hit the canonical lookup
//     "find the active thread for this project" in one step.
//   • `updated_at` already exists; no-op here but restated for clarity.
//
// Upgrade populates missing fields on pre-v20 rows:
//   • status defaults to 'active' (pre-wave-2 rows were effectively active).
//   • compacted_summary = null.
//   • compacted_up_to = 0 (nothing compacted yet).
//   • token_count = 0 (will be refreshed on next turn).
//
// NO DATA LOSS. BulkPut re-writes the same rows with supplemental fields.
db.version(20).stores({
    // Re-declared with new indexes: Dexie preserves existing rows when you
    // change the index schema without touching the primary key.
    ai_conversations: '++id, projectId, status, updated_at, [projectId+status]',
}).upgrade(async tx => {
    const table = tx.table('ai_conversations');
    const old = await table.toArray();
    if (old.length === 0) {
        console.log('[DB] v20: ai_conversations indexes upgraded (no existing rows to backfill)');
        return;
    }
    await table.bulkPut(old.map(c => ({
        ...c,
        status:             c.status            || 'active',
        compacted_summary:  c.compacted_summary ?? null,
        compacted_up_to:    typeof c.compacted_up_to === 'number' ? c.compacted_up_to : 0,
        token_count:        typeof c.token_count       === 'number' ? c.token_count       : 0,
    })));
    console.log(`[DB] v20: ai_conversations extended for wave-2 assistant (status/compacted_*/token_count backfilled on ${old.length} row(s))`);
});

// ============================================
// Dexie v21: Plausibility Fix Center resolutions (Wave 3)
// ============================================
// why: persist per-flag status (fixed/ignored/acknowledged) so re-analysis can
// filter already-resolved flags and the banner stops nagging.
db.version(21).stores({
    plausibility_resolutions: 'id, [projectId+flagHash], status, updated_at',
}).upgrade(tx => {
    console.log('[DB] v21: plausibility_resolutions table created (Wave 3 Fix Center)');
});

// ============================================
// Dexie v22: Pending-writes outbox (DATA-4)
// ============================================
// why: when the API circuit-breaker trips, writes fall back to Dexie but were
// never replayed on backend recovery: the two stores silently diverged. The
// outbox queues failed writes `{id, key, method, args, ts}` and drains them
// when _apiFailCount resets. The idempotency key is the row's uuid so a
// replayed write doesn't double-create.
db.version(22).stores({
    pending_writes: 'id, ts, key',
}).upgrade(tx => {
    console.log('[DB] v22: pending_writes outbox created (DATA-4 dual-store replay)');
});

// ============================================
// Dexie v23: batch_jobs queue store (BQ-CP-3)
// ============================================
// why: Phase 2 batch quote processor needs durable queue state so tab-close
// mid-batch doesn't lose work. Each row is one project's run through the
// Stage 1→5 pipeline. State machine: pending → processing → completed |
// failed (retry-able) | skipped | cancelled. See PHASE-2-QUEUE-DESIGN.md
// §4 for the binding schema + state diagram, src/services/batch-queue.js
// for the orchestrator that reads/writes this store, and DEC-BQ-007 for
// why the queue is strictly sequential per tab.
db.version(23).stores({
    batch_jobs: '++id, projectId, batchId, status, position, enqueuedAt',
}).upgrade(tx => {
    console.log('[DB] v23: batch_jobs queue store created (BQ-CP-3 Phase 2 Foundation)');
});

// ============================================
// Dexie v24: batch_jobs.phase field (DEC-TQ-001)
// ============================================
// why: Two-stage queue split per docs/ai-pipeline-council/two-stage-queue/.
// Each batch_job row carries a `phase` indicating whether it represents a
// pre-processing job (deterministic intake — text extract, photo persist,
// status flip to ready_for_analysis) or a prepare-quote job (the existing
// AI Stage 1→5 cascade). Single store + phase field beats two stores: one
// orchestrator, one set of CustomEvents, simple auto-promote (DEC-TQ-004).
// Existing rows back-fill to 'prepare-quote' so the upgrade is invisible
// to in-flight queues.
db.version(24).stores({
    batch_jobs: '++id, projectId, batchId, status, phase, position, enqueuedAt',
}).upgrade(tx => {
    console.log('[DB] v24: batch_jobs.phase index added (DEC-TQ-001 two-stage queue split)');
    return tx.batch_jobs.toCollection().modify(row => {
        if (!row.phase) row.phase = 'prepare-quote';
    });
});

// ============================================
// Dexie v25: Receivables Intelligence static-import cases
// ============================================
// why: Phase 1 of the payment-recovery workbench needs a browser-local case
// register for prototype CSV/JSON imports before any Xero or email connector
// writes are introduced. Xero and email remain read-only; this table stores
// imported invoice facts plus human investigation notes/status/follow-up.
db.version(25).stores({
    receivable_cases: 'id, invoiceNumber, status, dueDateIso, daysOverdue, company, sp, nextActionDate, updated_at',
}).upgrade(tx => {
    console.log('[DB] v25: receivable_cases table created (Receivables Intelligence static import)');
});

// ============================================
// Dexie v26: Receivables Intelligence integration placeholders
// ============================================
// why: The Command Queue UI now makes the intended feature areas explicit:
// Xero read-only sync, email ingestion, persisted project links, evidence rows,
// and a human action register. These tables are intentionally empty scaffolds
// until the read-only connectors are built, but naming them now keeps the UI,
// assistant, and future backend bridge aligned on the same data contract.
db.version(26).stores({
    receivable_evidence_items: 'id, caseId, sourceType, sourceId, occurredAt, importedAt, [caseId+sourceType]',
    receivable_project_links: 'id, caseId, projectId, confidence, score, created_at, [caseId+projectId]',
    receivable_actions: 'id, caseId, actionType, status, dueAt, created_at, completedAt, owner',
    receivable_sync_state: 'id, provider, tenantId, status, lastReadAt, updated_at',
}).upgrade(tx => {
    console.log('[DB] v26: receivables evidence/link/action/sync placeholder tables created');
});

// ============================================
// Dexie v27: Training Studio v2 — additive schema (BT-CP-2a)
// ============================================
// why: Replaces the legacy training surface (learnings/gold_standards/learning_events/
// learning_outcomes/training_snapshots/pricing_learnings/project_learnings/proposals)
// with a richer, facet-typed corpus. CP-2a is ADDITIVE only — legacy stores stay so the
// live AI pipeline (Stage 2 risk + Stage 5 quote gen) continues to read its current
// canon while CP-3..CP-9 build the ingestion + retrieval rewire. The destructive cutover
// (legacy drop + JSON archive) lands in CP-2b after the new pipeline is proven.
//
// Schema follows the council-verified design:
//   - Three-role entity model (managing_company / owner_corporation / end_client) with
//     strata_plan_number as the natural key for OC dedup ("SP55288" canonical form).
//   - Split outcome (qm_status = lifecycle, sales_outcome = WON/LOST/SENT).
//   - Revisions iterative not superseding (Rev1.0/Rev2.0 both preserved).
//   - Attachments table for asbestos/roof/structural reports + .msg emails.
//   - Insight = derived knowledge (facet-typed; replaces the flat learning enum).
//   - Materialized facet index for fast Library filters.
//   - training_run carries cross-tab heartbeat for safe-failure batch ingestion.
//
// Indexed-but-not-yet-populated columns (OF-TS-007/008 deferred state):
//   - tenant_id is now stamped on every quote_corpus.save (OF-TS-007 closed
//     2026-05-10) — `getActiveTenantId()` from tenant-resolver.js.
//   - building_group_key is INTENTIONALLY null until the strata three-role
//     entity model lands. The column groups multiple Strata Plans into the
//     same physical building cluster (e.g. "Bondi U11" spanning SP12345 +
//     SP12346); without an entity-model resolver there's nothing to compute
//     it from. Tracked as OF-TS-008 in CP-10-verification-pack.md.
db.version(27).stores({
    quote_corpus: 'id, tenant_id, managing_company_id, owner_corporation_id, end_client_company_id, strata_plan_number, region, suburb, project_type, scale_tier, qm_status, sales_outcome, sent_at, won_at, lost_at, imported_at, imported_via, training_run_id, user_verified, needs_review, qm_project_id, building_group_key, urgency, is_requote, is_insurance_claim, is_common_property, *assessor_initials, *work_type_tags, [project_type+scale_tier+region], [managing_company_id+project_type], [sales_outcome+sent_at], [strata_plan_number+sent_at]',
    corpus_scope_item: '++id, corpus_id, [corpus_id+index], trade_category, work_type, [trade_category+work_type], pricing_method, was_in_rfq, is_proactive_addition, is_value_added',
    corpus_exclusion: '++id, corpus_id, category, is_universal',
    corpus_voice_sample: '++id, corpus_id, sample_type, *voice_attributes',
    corpus_revision: '++id, parent_corpus_id, revision_label, is_latest, [parent_corpus_id+is_latest], created_at',
    corpus_attachment: '++id, corpus_id, kind, [corpus_id+kind]',
    corpus_facet_index: '++id, corpus_id, facet_type, facet_value, [facet_type+facet_value]',
    insight: 'id, facet, status, active, confidence, support_count, last_seen_at, [facet+active], [status+confidence], *support_corpus_ids',
    insight_application: '++id, insight_id, project_id, [insight_id+project_id], stage_name, injected_at, outcome, outcome_recorded_at, [outcome+outcome_recorded_at]',
    insight_proposal: 'id, status, queued_at, *source_corpus_ids, [status+queued_at]',
    training_run: 'id, started_at, completed_at, status, last_heartbeat_at, holder_tab_id, model_locked, [status+started_at]',
}).upgrade(tx => {
    console.log('[DB] v27: Training Studio v2 schema added (11 stores, additive — legacy stores preserved)');
});

// ============================================
// Dexie v28: Training Studio v2 batch trainer queue (BT-CP-7a)
// ============================================
// why: The training batch queue is a separate state machine from the quote-
// generation queue (which lives at `batch_jobs` v23). Different state machine
// (extract → tag → mine → save vs Stage 1→5), different per-job cost tracking,
// different concurrency guarantees. Sharing `batch_jobs` would force every
// quote-gen consumer to learn about training-only fields.
//
// Schema fields:
//   id              — string PK, batch_training_jobs row identifier
//   batchId         — groups jobs that were enqueued together (one batch = N jobs)
//   sourcePath      — folder or file the job ingests from
//   format          — QM_SYNC_NATIVE | PAIRED_RAW_BUNDLE | FLAT_QUOTE_PDF | FLAT_RFQ_PDF
//   status          — pending | processing | completed | failed | skipped | cancelled
//   position        — display order within the batch (1-based)
//   stage           — current internal stage (read | extract | tag | mine | save)
//   corpusId        — populated once the corpus row is written (for drill-back)
//   estimatedCostUsd — running total committed by cost-tracker
//   actualCostUsd   — finalised cost when status = completed | failed
//   error           — last error message when status = failed
//   enqueuedAt, startedAt, completedAt — timestamps
//   trainingRunId   — FK to training_run for grouping
db.version(28).stores({
    batch_training_jobs: 'id, batchId, sourcePath, format, status, position, stage, corpusId, trainingRunId, enqueuedAt, startedAt, completedAt, [batchId+position], [status+enqueuedAt], [trainingRunId+status]',
}).upgrade(tx => {
    console.log('[DB] v28: batch_training_jobs created (Training Studio v2 BT-CP-7a)');
});

// ============================================
// versionchange handler — multi-tab safety
// ============================================
// Without this, an old tab holding the DB at vN will deadlock a new tab opening at vN+1.
// Dexie raises versionchange on the old tab; we close to let the upgrade proceed.
// Added 2026-05-10 alongside BT-CP-2a per migration-risk specialist finding.
db.on('versionchange', () => {
    try {
        console.warn('[DB] versionchange detected — closing this tab\'s connection so the upgrade can proceed');
        db.close();
    } catch (err) {
        console.warn('[DB] versionchange close failed:', err?.message);
    }
});

// ============================================
// Dexie v29 — File-explorer index (Phase A of file-explorer-redesign)
// ============================================
// why: replace the live-FSA-walk model (cap maxFiles=300/project at
// file-explorer-intake.js:725) with a persistent Dexie-backed index so
// re-opens are sub-500ms and the cap can be removed without a perf
// regression. Mirrors IIB's image/folder schema pattern, adapted for
// QuoteMaker's project-centric data model. See
// docs/file-explorer-redesign/PHASE-A-BINDING-SPEC.md (Council of 7
// ratified) and IIB-WEBAPP-PORT.md for the eight-phase plan.
//
// NOTE on version number: this worktree branched from v5.19.41 (HEAD = v26)
// and originally declared v27 in the worktree. At integration the schema
// was renumbered to v29 because main had already shipped v27 (Training
// Studio v2 BT-CP-2a, 355a4f21) and v28 (batch trainer queue BT-CP-7a,
// eeb80299). The audit constant moved from `fe.audit.migration:27` to
// `fe.audit.migration:29` in lockstep across db.js, file-explorer-migration.js,
// DeveloperPage.jsx purge action, and the test fixtures.
//
// Schema choices the Council ratified:
// - fe_root.handle is a non-indexed column (FSA structured-clone supported
//   since Chrome 122+ per sync-folder.js:9-12).
// - fe_file omits forward-compat columns (cloud_state, embedding fields,
//   thumb refs) — those land per-phase. One-bump-one-feature discipline.
// - No separate fe_setting table; per-key state goes into app_settings
//   under the `fe.` prefix (e.g. `fe.phase_a_seeded`,
//   `fe.audit.migration:29`, `fe.last_walked_at:<rootId>`). This avoids
//   threading a 4th table through the assistant + DB Explorer whitelists.
// - enabled column uses true/false (QM convention verified at
//   ai-learning.js:141, crm-migration.js:178, learning-harvest.js:1036).
// - docClassConfidence is integer 0-100, not 0-1
//   (document-type-classifier.js:170/175/242/251/256/261/265).
//
// Migration:
// - Reads existing app_settings.syncFolderHandle (the legacy source of
//   truth — sync-folder.js:331-376 is the only direct reader/writer).
// - Mirrors it into fe_root[0] WITHOUT deleting the source. Sync-folder.js
//   continues to read app_settings; fe_root[0] is a derived mirror.
//   Rollback v29 → v28 is data-loss-free; sync-folder.js needs zero
//   changes for Phase A.
// - Sentinel `fe.phase_a_seeded` short-circuits double-runs.
// - Audit row at `fe.audit.migration:29` records when migration ran
//   (resolves Council seat 3 finding F-T2 + seat 6 telemetry gap).
db.version(29).stores({
    fe_root: '++id, &label, addedAt, enabled, lastFullWalkAt',
    fe_file: '++id, &[rootId+relPath], rootId, parentDir, name, ext, kind, docClass, docClassifiedAt, sizeBytes, mtime, contentHash, lastSeenAt, addedAt, [rootId+kind], [rootId+parentDir]',
    fe_walk_run: '++id, rootId, status, startedAt, finishedAt',
}).upgrade(async tx => {
    console.log('[DB] v29: file-explorer index stores created (Phase A of file-explorer-redesign)');
    const result = await seedPhaseAFeRoot(tx);
    console.log(`[DB] v29: Phase A seed result —`, result);
});

// v30: Document Blob storage — mirror of photo_blobs (v13) / video_blobs (v17)
// for reference-document binaries (PDF / DOCX). Gives reference files the same
// durable local backing photos have: a doc whose S3 upload failed, or a DOCX
// (which is never uploaded to S3), keeps its bytes in IndexedDB so it can still
// be previewed after reload — closing the "reference files disappear from the
// Cockpit" gap. Additive table; rollback v30 → v29 drops only the local
// doc-blob cache and never touches project records.
db.version(30).stores({
    document_blobs: '++id, projectId',
}).upgrade(() => {
    console.log('[DB] v30: document_blobs table created');
});

// v31: tasks — the operator's to-do list (Tasks page slice 1, per
// docs/design/SPEC-tasks-page.md §6.3). Browser-local until the server floor
// (migration 085_tasks.sql, slice 4) and the promotion to synced (slice 5)
// land; every DataService method routes to Dexie via LOCAL_ONLY_METHODS.
//
// `id`, NOT `++id`: the client generates the UUID (crypto.randomUUID) so
// Dexie and Postgres share ONE identity. This is the deliberate fix for the
// crm_activities PK mismatch (UUID server-side vs ++id local) that forces
// resolveDexieId. Index names are camelCase because createProxy dual-writes
// the CALLER's args and callers pass camelCase; crm_activities uses
// snake_case indexes only because it predates the convention.
db.version(31).stores({
    tasks: 'id, status, dueOn, todaySetOn, projectId, sourceKey, createdAt, [status+todaySetOn]',
}).upgrade(() => {
    console.log('[DB] v31: tasks store created');
});

// Browser builds keep every local table closed until /auth/me verifies the
// immutable tenant binding captured above. Vitest keeps Dexie's normal
// auto-open behaviour so isolated service tests can inject their own tables;
// the production lock is covered by the compiled-browser acceptance lane.
const ENFORCE_VERIFIED_LOCAL_DATABASE = import.meta.env?.MODE !== 'test';
if (ENFORCE_VERIFIED_LOCAL_DATABASE) {
    db.close({ disableAutoOpen: true });
}

export async function openLocalDatabaseForVerifiedTenant(serverIdentity) {
    markLocalTenantSessionVerified(serverIdentity, LOCAL_DATABASE_IDENTITY);
    try {
        if (!db.isOpen()) await db.open();
        return db;
    } catch (error) {
        clearLocalTenantSessionVerification();
        throw error;
    }
}

export async function upgradeLegacyLocalDataForVerifiedTenant(ownershipEnvelope) {
    const verifiedTenantId = getVerifiedLocalTenantId();
    return upgradeVerifiedLegacyLocalData({
        targetDb: db,
        verifiedTenantId,
        ownershipTenantId: ownershipEnvelope?.tenantId,
        ownedProjects: ownershipEnvelope?.projects,
    });
}

export function closeLocalTenantDatabase() {
    clearLocalTenantSessionVerification();
    db.close({ disableAutoOpen: ENFORCE_VERIFIED_LOCAL_DATABASE });
}

export default db;
